import { apiClient, resolveApiUrl } from '@/features/shared/api/client';
import type {
  AttemptDetail,
  AttemptHistoryItem,
  PracticeAttemptPayload,
  PracticeAttemptTranscript,
  PracticeQuestionData,
  PracticeQuestionCatalogItem,
  PracticeQuestionNavigationSummary,
  AttemptSubmitResult,
  PracticeMode,
  PracticeRecordingUploadResult,
  PracticeRecordingUploadSession,
  ReferenceAnswer,
  ResourceAccessRefreshResult,
  RenderMode,
  StudentQuestionSetDetail,
  StudentQuestionSetSummary,
} from '../types/practice';
import type { Question, QuestionType } from '@/features/shared/types/question';

export interface PracticeAttemptResponse {
  attemptId: string;
  status: string;
  renderMode: RenderMode;
  practiceMode: PracticeMode;
  questionSetId?: string | null;
  resourceAccessExpiresAt?: string | null;
  startedAt?: string | null;
  resumedAttempt?: boolean;
}

export interface PracticeSessionResponse {
  id: string;
  mode: string;
  status: string;
  questionSetId?: string | null;
  startedAt?: string | null;
}

export interface UserProgressItem {
  questionId: string;
  questionTypeCode: string;
  title?: string | null;
  status: string;
  attemptCount: number;
  lastAttemptId?: string | null;
  firstDoneAt?: string | null;
  lastDoneAt?: string | null;
  isHighFrequency?: boolean;
}

export interface UserStats {
  doneQuestionCount: number;
  totalAttemptCount: number;
  questionTypeCompletionCount: Array<{
    questionTypeCode: string;
    doneCount: number;
  }>;
  recentAttempts: Array<{
    attemptId: string;
    questionId: string;
    questionTypeCode: string;
    title?: string | null;
    status: string;
    submittedAt?: string | null;
    durationMs?: number | null;
  }>;
}

export interface PeriodAnalyticsMetric {
  value: number;
  previousValue?: number | null;
  comparable: boolean;
  changePercent?: number | null;
  direction?: 'up' | 'down' | 'flat' | null;
  rankNo?: number | null;
  rankSampleSize?: number | null;
}

export interface PeriodAnalyticsOverview {
  range: 'all' | 'day' | 'week' | 'month';
  compareMode: 'previous_period' | 'year_over_year' | 'none';
  currentStart: string;
  currentEnd: string;
  comparisonStart?: string | null;
  comparisonEnd?: string | null;
  metrics: Record<string, PeriodAnalyticsMetric>;
  generatedAt: string;
}

function normalizeQuestion(detail: PracticeQuestionData): PracticeQuestionData {
  return {
    ...detail,
    bundleFallback: detail.bundleFallback
      ? {
          ...detail.bundleFallback,
          entryUrl: resolveApiUrl(detail.bundleFallback.entryUrl) || detail.bundleFallback.entryUrl,
        }
      : detail.bundleFallback,
    resourceAccess: {
      ...detail.resourceAccess,
      assets: detail.resourceAccess.assets.map((asset) => ({
        ...asset,
        url: asset.url ? (resolveApiUrl(asset.url) || asset.url) : asset.url,
        protectedAudio: asset.protectedAudio
          ? {
              ...asset.protectedAudio,
              manifestUrl: resolveApiUrl(asset.protectedAudio.manifestUrl) || asset.protectedAudio.manifestUrl,
            }
          : asset.protectedAudio,
      })),
    },
  };
}

function normalizeResourceAccessRefresh(result: ResourceAccessRefreshResult): ResourceAccessRefreshResult {
  return {
    ...result,
    assets: result.assets.map((asset) => ({
      ...asset,
      url: asset.url ? (resolveApiUrl(asset.url) || asset.url) : asset.url,
      protectedAudio: asset.protectedAudio
        ? {
            ...asset.protectedAudio,
            manifestUrl: resolveApiUrl(asset.protectedAudio.manifestUrl) || asset.protectedAudio.manifestUrl,
          }
        : asset.protectedAudio,
    })),
  };
}

function normalizeSubmitResult(result: AttemptSubmitResult): AttemptSubmitResult {
  if (!result.referenceAnswer) {
    return result;
  }
  return {
    ...result,
    referenceAnswer: normalizeReferenceAnswer(result.referenceAnswer),
  };
}

function normalizeReferenceAnswer(result: ReferenceAnswer): ReferenceAnswer {
  return {
    ...result,
    answerAudioUrl: result.answerAudioUrl
      ? (resolveApiUrl(result.answerAudioUrl) || result.answerAudioUrl)
      : result.answerAudioUrl,
    answerAudioProtected: normalizeProtectedAudioSource(result.answerAudioProtected),
    keywordAudioAssets: result.keywordAudioAssets?.map((asset) => ({
      ...asset,
      url: asset.url ? (resolveApiUrl(asset.url) || asset.url) : asset.url,
      protectedAudio: normalizeProtectedAudioSource(asset.protectedAudio),
    })),
  };
}

function normalizeProtectedAudioSource<T extends { manifestUrl?: string | null } | null | undefined>(value: T): T {
  if (!value?.manifestUrl) {
    return value;
  }
  return {
    ...value,
    manifestUrl: resolveApiUrl(value.manifestUrl) || value.manifestUrl,
  };
}

function normalizeRecordingUploadResult(result: PracticeRecordingUploadResult): PracticeRecordingUploadResult {
  return {
    ...result,
    protectedAudio: normalizeProtectedAudioSource(result.protectedAudio),
  };
}

function normalizeRecordingUploadSession(result: PracticeRecordingUploadSession | null): PracticeRecordingUploadSession | null {
  if (!result) {
    return null;
  }
  return {
    ...result,
    recording: result.recording ? normalizeRecordingUploadResult(result.recording) : result.recording,
  };
}

function normalizeAttemptDetail(detail: AttemptDetail): AttemptDetail {
  return {
    ...detail,
    questionResourceAccess: detail.questionResourceAccess
      ? {
          ...detail.questionResourceAccess,
          assets: detail.questionResourceAccess.assets.map((asset) => ({
          ...asset,
          url: asset.url ? (resolveApiUrl(asset.url) || asset.url) : asset.url,
          protectedAudio: asset.protectedAudio
            ? {
                ...asset.protectedAudio,
                manifestUrl: resolveApiUrl(asset.protectedAudio.manifestUrl) || asset.protectedAudio.manifestUrl,
              }
            : asset.protectedAudio,
        })),
      }
    : detail.questionResourceAccess,
    recording: detail.recording
      ? {
          ...detail.recording,
          protectedAudio: detail.recording.protectedAudio
            ? {
                ...detail.recording.protectedAudio,
                manifestUrl:
                  resolveApiUrl(detail.recording.protectedAudio.manifestUrl)
                  || detail.recording.protectedAudio.manifestUrl,
              }
            : detail.recording.protectedAudio,
        }
      : detail.recording,
    referenceAnswer: detail.referenceAnswer ? normalizeReferenceAnswer(detail.referenceAnswer) : detail.referenceAnswer,
  };
}

async function getQuestionPage(
  questionTypeCode?: string,
  page = 1,
  pageSize = 12,
  prioritizeHighFrequency = false,
  frequencyScope: 'all' | 'high_frequency' | 'regular' = 'all',
  newOnly = false,
  init?: RequestInit,
) {
  const search = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  if (questionTypeCode) {
    search.set('questionTypeCode', questionTypeCode);
  }
  if (prioritizeHighFrequency) {
    search.set('prioritizeHighFrequency', 'true');
  }
  if (frequencyScope !== 'all') search.set('frequencyScope', frequencyScope);
  if (newOnly) search.set('newOnly', 'true');
  return apiClient.get<{ items: Question[]; total: number; page: number; pageSize: number }>(
    `/questions?${search.toString()}`,
    init,
  );
}

const API = {
  getQuestionTypes: (init?: RequestInit) => apiClient.get<QuestionType[]>('/question-types', init),
  getQuestionCatalog: (
    paramsOrInit?: { includeItems?: boolean; prioritizeHighFrequency?: boolean; frequencyScope?: 'all' | 'high_frequency' | 'regular'; newOnly?: boolean } | RequestInit,
    init?: RequestInit,
  ) => {
    const params = paramsOrInit && !('signal' in paramsOrInit)
      ? paramsOrInit as { includeItems?: boolean; prioritizeHighFrequency?: boolean; frequencyScope?: 'all' | 'high_frequency' | 'regular'; newOnly?: boolean }
      : undefined;
    const requestInit = params ? init : paramsOrInit as RequestInit | undefined;
    const search = new URLSearchParams({
      includeItems: params?.includeItems === false ? 'false' : 'true',
    });
    if (params?.prioritizeHighFrequency) {
      search.set('prioritizeHighFrequency', 'true');
    }
    if (params?.frequencyScope && params.frequencyScope !== 'all') search.set('frequencyScope', params.frequencyScope);
    if (params?.newOnly) search.set('newOnly', 'true');
    return apiClient.get<PracticeQuestionCatalogItem[]>(`/questions/catalog?${search.toString()}`, requestInit);
  },
  getQuestionTypeCatalog: (
    questionTypeCode: string,
    params?: { page?: number; pageSize?: number; currentQuestionId?: string; prioritizeHighFrequency?: boolean; frequencyScope?: 'all' | 'high_frequency' | 'regular'; newOnly?: boolean },
    init?: RequestInit,
  ) => {
    const search = new URLSearchParams({
      questionTypeCode,
      page: String(params?.page || 1),
      pageSize: String(params?.pageSize || 40),
    });
    if (params?.currentQuestionId) {
      search.set('currentQuestionId', params.currentQuestionId);
    }
    if (params?.prioritizeHighFrequency) {
      search.set('prioritizeHighFrequency', 'true');
    }
    if (params?.frequencyScope && params.frequencyScope !== 'all') search.set('frequencyScope', params.frequencyScope);
    if (params?.newOnly) search.set('newOnly', 'true');
    return apiClient.get<{
      items: PracticeQuestionCatalogItem[];
      page: number;
      pageSize: number;
      total: number;
    }>(`/questions/navigation-items?${search.toString()}`, init);
  },
  getQuestionNavigationSummary: (init?: RequestInit) =>
    apiClient.get<PracticeQuestionNavigationSummary[]>('/questions/navigation-summary', init),
  getQuestions: (
    params?: { questionTypeCode?: string; page?: number; pageSize?: number; prioritizeHighFrequency?: boolean; frequencyScope?: 'all' | 'high_frequency' | 'regular'; newOnly?: boolean },
    init?: RequestInit,
  ) => getQuestionPage(
    params?.questionTypeCode,
    params?.page || 1,
    params?.pageSize || 12,
    params?.prioritizeHighFrequency === true,
    params?.frequencyScope || 'all',
    params?.newOnly === true,
    init,
  ),
  getQuestionSets: (params?: { page?: number; pageSize?: number }, init?: RequestInit) => {
    const search = new URLSearchParams({
      page: String(params?.page || 1),
      pageSize: String(params?.pageSize || 6),
    });
    return apiClient.get<{ items: StudentQuestionSetSummary[]; total: number; page: number; pageSize: number }>(
      `/question-sets?${search.toString()}`,
      init,
    );
  },
  getQuestionSetDetail: (setId: string, init?: RequestInit) => apiClient.get<StudentQuestionSetDetail>(`/question-sets/${setId}`, init),
  getQuestion: (
    id: string,
    options?: { questionSetId?: string | null; prioritizeHighFrequency?: boolean; frequencyScope?: 'all' | 'high_frequency' | 'regular'; newOnly?: boolean },
    init?: RequestInit,
  ) => {
    const search = new URLSearchParams();
    if (options?.questionSetId) {
      search.set('questionSetId', options.questionSetId);
    }
    if (options?.prioritizeHighFrequency && !options.questionSetId) {
      search.set('prioritizeHighFrequency', 'true');
    }
    if (options?.frequencyScope && options.frequencyScope !== 'all' && !options.questionSetId) search.set('frequencyScope', options.frequencyScope);
    if (options?.newOnly && !options.questionSetId) search.set('newOnly', 'true');
    const suffix = search.toString() ? `?${search.toString()}` : '';
    return apiClient.get<PracticeQuestionData>(`/questions/${id}${suffix}`, init).then(normalizeQuestion);
  },
  getReferenceAnswerPreview: (attemptId: string, init?: RequestInit) =>
    apiClient
      .get<ReferenceAnswer>(`/practice-attempts/${attemptId}/reference-answer-preview`, init)
      .then(normalizeReferenceAnswer),
  getAttemptTranscript: (attemptId: string, init?: RequestInit) =>
    apiClient.get<PracticeAttemptTranscript>(`/practice-attempts/${attemptId}/transcript`, init),
  createPracticeSession: (payload?: { mode?: string; questionSetId?: string | null }, init?: RequestInit) =>
    apiClient.post<PracticeSessionResponse>('/practice-sessions', payload ?? { mode: 'single_question' }, init),
  createAttempt: (payload: PracticeAttemptPayload, init?: RequestInit) => apiClient.post<PracticeAttemptResponse>('/practice-attempts', payload, init),
  refreshAttemptResourceAccess: (
    attemptId: string,
    payload: {
      questionId: string;
      renderMode: RenderMode;
      componentSchemaId?: string | null;
      requestedAssetIds?: string[];
    },
  ) =>
    apiClient.post<ResourceAccessRefreshResult>(`/practice-attempts/${attemptId}/resource-access/refresh`, {
      questionId: payload.questionId,
      renderMode: payload.renderMode,
      componentSchemaId: payload.componentSchemaId,
      requestedAssetIds: payload.requestedAssetIds || [],
    }).then(normalizeResourceAccessRefresh),
  getActiveRecordingUploadSession: (attemptId: string, init?: RequestInit) =>
    apiClient
      .get<PracticeRecordingUploadSession | null>(`/practice-attempts/${attemptId}/recording-upload-sessions/active`, init)
      .then(normalizeRecordingUploadSession),
  createRecordingUploadSession: (
    attemptId: string,
    payload: {
      fileName?: string;
      contentType?: string;
    },
    init?: RequestInit,
  ) =>
    apiClient
      .post<PracticeRecordingUploadSession>(`/practice-attempts/${attemptId}/recording-upload-sessions`, payload, init)
      .then((result) => normalizeRecordingUploadSession(result) as PracticeRecordingUploadSession),
  uploadRecordingChunk: (
    attemptId: string,
    uploadSessionId: string,
    payload: {
      blob: Blob;
      chunkIndex: number;
      fileName?: string;
    },
    init?: RequestInit,
  ) => {
    const formData = new FormData();
    formData.append('file', payload.blob, payload.fileName || `chunk-${payload.chunkIndex}.webm`);
    formData.append('chunkIndex', String(payload.chunkIndex));
    if (payload.fileName) {
      formData.append('fileName', payload.fileName);
    }
    return apiClient
      .post<PracticeRecordingUploadSession>(
        `/practice-attempts/${attemptId}/recording-upload-sessions/${uploadSessionId}/chunks`,
        formData,
        init,
      )
      .then((result) => normalizeRecordingUploadSession(result) as PracticeRecordingUploadSession);
  },
  finalizeRecordingUploadSession: (
    attemptId: string,
    uploadSessionId: string,
    payload: {
      fileName?: string;
      contentType?: string;
      durationSeconds?: number;
    },
    init?: RequestInit,
  ) =>
    apiClient
    .post<PracticeRecordingUploadResult | PracticeRecordingUploadSession>(
      `/practice-attempts/${attemptId}/recording-upload-sessions/${uploadSessionId}/finalize`,
      {
        fileName: payload.fileName,
        contentType: payload.contentType,
        durationSeconds: payload.durationSeconds,
      },
      init,
    )
      .then((result) => (
        'recordingHandle' in result
          ? normalizeRecordingUploadResult(result)
          : normalizeRecordingUploadSession(result) as PracticeRecordingUploadSession
      )),
  abandonRecordingUploadSession: (
    attemptId: string,
    uploadSessionId: string,
    reason = 'discarded_by_student',
    init?: RequestInit,
  ) => {
    const formData = new FormData();
    formData.append('reason', reason);
    return apiClient
      .post<PracticeRecordingUploadSession>(
        `/practice-attempts/${attemptId}/recording-upload-sessions/${uploadSessionId}/abandon`,
        formData,
        init,
      )
      .then((result) => normalizeRecordingUploadSession(result) as PracticeRecordingUploadSession);
  },
  markAttemptAbnormalEnd: (
    attemptId: string,
    payload: {
      reason: string;
      hiddenDurationMs?: number;
      uploadSessionId?: string | null;
    },
    init?: RequestInit,
  ) => apiClient.post<{ attemptId: string; status: string; reason: string }>(
    `/practice-attempts/${attemptId}/abnormal-end`,
    payload,
    init,
  ),
  submitAttempt: (
    attemptId: string,
    payload: {
      questionId: string;
      renderMode: RenderMode;
      componentSchemaId?: string | null;
      bundleId?: string | null;
      eventType: string;
      clientEventId: string;
      durationMs: number;
      legacy?: Record<string, unknown>;
      payload?: Record<string, unknown>;
      securitySignals?: Record<string, unknown>;
    },
  ) => apiClient.post<AttemptSubmitResult>(`/practice-attempts/${attemptId}/submit`, payload).then(normalizeSubmitResult),
  getAttempts: (params?: { questionTypeCode?: string; page?: number; pageSize?: number; cursor?: string }, init?: RequestInit) => {
    const search = new URLSearchParams({
      page: String(params?.page || 1),
      pageSize: String(params?.pageSize || 20),
    });
    if (params?.questionTypeCode) {
      search.set('questionTypeCode', params.questionTypeCode);
    }
    if (params?.cursor) {
      search.set('cursor', params.cursor);
    }
    return apiClient.get<{ items: AttemptHistoryItem[]; total: number; page: number; pageSize: number; nextCursor?: string | null }>(
      `/me/attempts?${search.toString()}`,
      init,
    );
  },
  getAttemptDetail: (attemptId: string, init?: RequestInit) =>
    apiClient.get<AttemptDetail>(`/me/attempts/${attemptId}`, init).then(normalizeAttemptDetail),
  getProgress: (init?: RequestInit) => apiClient.get<UserProgressItem[]>('/me/progress', init),
  getStats: (init?: RequestInit) => apiClient.get<UserStats>('/me/stats', init),
  getPeriodAnalyticsOverview: (
    range: 'all' | 'day' | 'week' | 'month' = 'week',
    compareMode: 'previous_period' | 'year_over_year' | 'none' = 'previous_period',
    init?: RequestInit,
  ) => apiClient.get<PeriodAnalyticsOverview>(`/me/analytics/period-overview?range=${range}&compareMode=${compareMode}`, init),
};

export default API;
